import { useId } from 'react';
import type { ReactNode } from 'react';
import Popover from './ui/Popover';

interface HelpHintProps {
  /**
   * Short topic name used in the aria-label and as a fallback heading
   * inside the popover. Keep it short — appears in screen reader output.
   */
  topic: string;
  /** The popover body. Keep copy to 1-2 sentences plus an optional anchor link. */
  children: ReactNode;
  /**
   * Optional className passed to the wrapping span so each call site can
   * tweak inline positioning (e.g., align with a label, sit on its own row).
   */
  className?: string;
}

/**
 * Small inline help-hint primitive: an info icon button that opens a
 * Popover with explanatory text. Used to surface key bits of the
 * moderator guide right next to controls instead of forcing moderators
 * to open the standalone guide page.
 *
 * Pair every HelpHint with a link to the relevant in-tool guide anchor
 * so moderators can dive deeper without losing the workbench context.
 */
export default function HelpHint({ topic, children, className }: HelpHintProps) {
  const surfaceId = useId();
  return (
    <span className={`help-hint${className ? ` ${className}` : ''}`}>
      <Popover
        describedById={surfaceId}
        ariaLabel={`Help: ${topic}`}
        trigger={
          <button
            type="button"
            className="help-hint__trigger"
            aria-label={`More info: ${topic}`}
            data-testid={`help-hint-${topic.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <span aria-hidden="true">?</span>
          </button>
        }
      >
        <div className="help-hint__body">{children}</div>
      </Popover>
    </span>
  );
}
